import { useState, useRef, useEffect, useId, ReactNode } from 'react';

/**
 * AppInfoTooltip — Phase 2.x-c PR-B.
 *
 * Minimal accessible tooltip primitive for exposing an app's mode-resolved
 * `description` (from Application Registry v2). No external dependencies.
 *
 * The visible label (`children`) is authored inline by the caller so each
 * surface can render `displayName` + `subtitle` in the layout that fits it
 * (chip, row, tile). This component only owns the tooltip interaction:
 *
 *   - Desktop: opens on hover AND on keyboard focus.
 *   - Mobile / touch: opens on tap; closes on outside tap or Escape.
 *   - Keyboard: a focusable button; Escape closes; ARIA describedby wired.
 *   - Graceful fallback: if `description` is absent, renders `children`
 *     inside a plain non-interactive element with no tooltip affordance.
 *
 * Uses `role="tooltip"` and `aria-describedby` when open, matching the
 * WAI-ARIA authoring practices for a description tooltip.
 */
export interface AppInfoTooltipProps {
  /**
   * The description surfaced in the tooltip. When omitted the component
   * renders a plain span with `children` inside (no button, no tooltip)
   * so callers can safely pass through registry entries that lack copy.
   */
  description?: string;
  /**
   * The visible label. Authored inline by the caller — typically an
   * `displayName` with an optional `subtitle` beneath or beside it.
   */
  children: ReactNode;
  /**
   * Tailwind class applied to the interactive trigger (or fallback span).
   */
  className?: string;
  /**
   * Where the tooltip floats relative to the trigger. Defaults to bottom.
   */
  side?: 'top' | 'bottom';
  /**
   * Optional data-testid hook for tests.
   */
  testId?: string;
}

export function AppInfoTooltip({
  description,
  children,
  className,
  side = 'bottom',
  testId,
}: AppInfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent | TouchEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // No description → no tooltip, no interactive affordance. Render a plain
  // span so the label still appears in its intended visual context.
  if (!description) {
    return (
      <span className={className} data-testid={testId}>
        {children}
      </span>
    );
  }

  const sideClasses = side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5';

  return (
    <span ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          // Prevent parent-card selection when this trigger lives inside
          // a card that has its own onClick (e.g. outcome-card selection).
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-describedby={open ? tipId : undefined}
        aria-expanded={open}
        className={className}
        data-testid={testId}
      >
        {children}
      </button>
      {open && (
        <span
          role="tooltip"
          id={tipId}
          className={`absolute left-0 z-20 w-64 max-w-[16rem] rounded-md bg-gray-900 px-3 py-2 text-xs leading-snug text-white shadow-lg ${sideClasses}`}
        >
          {description}
        </span>
      )}
    </span>
  );
}
